import { ref, shallowRef, computed } from 'vue';
import type {
  SlideCanvasBox,
  SlideCanvasPoint,
  SlideRegionDict,
  SlideRenderBox,
  SlideSelection,
  SlideRegionEntry,
} from '../components/SlideCanvas.types';
import { boundsToRect, createBoxId, rectToBounds, isPointInBox } from '../utils/slideGeometry';
import { normalizeRotation } from '../utils/slideRotation';

type LabelFilterMap = Record<string, boolean> | null;


function boundsToRenderBox(entry: SlideRegionEntry, label: string): SlideRenderBox {
  const rect = boundsToRect(entry.bounds);
  return {
    id: entry.id,
    label,
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    rotation: entry.rotation,
  };
}

export function useSlideRegions(getDefaultLabel: () => string) {
  const regions = ref<SlideRegionDict>({});
  const selected = ref<SlideSelection>(null);

  const renderBoxes = shallowRef<SlideRenderBox[]>([]);
  let labelFilter: LabelFilterMap = null;

  function isLabelVisible(label: string) {
    if (!labelFilter) return true;
    return labelFilter[label] !== false;
  }

  function ensureSelectionVisible() {
    if (!selected.value) return;
    const stillExists = renderBoxes.value.some(
      (box) => box.id === selected.value?.id && box.label === selected.value?.label,
    );
    if (!stillExists) {
      selected.value = null;
    }
  }

  function rebuildRenderBoxes() {
    const output: SlideRenderBox[] = [];
    Object.entries(regions.value).forEach(([label, entries]) => {
      if (!isLabelVisible(label)) {
        return;
      }
      entries.forEach((entry) => {
        output.push(boundsToRenderBox(entry, label));
      });
    });
    renderBoxes.value = output;
    ensureSelectionVisible();
  }

  function loadRegionsDict(next?: SlideRegionDict) {
    regions.value = next ?? {};
    rebuildRenderBoxes();
  }

  function addBox(box: SlideCanvasBox, label?: string) {
    const entry: SlideRegionEntry = {
      id: box.id ?? createBoxId(),
      bounds: rectToBounds(box),
      rotation: normalizeRotation(box.rotation ?? 0),
    };
    const key = label ?? getDefaultLabel();
    const bucket = regions.value[key] ? [...regions.value[key]!, entry] : [entry];
    regions.value = { ...regions.value, [key]: bucket };
    selected.value = { label: key, id: entry.id };
    const renderBox = boundsToRenderBox(entry, key);
    renderBoxes.value = [...renderBoxes.value, renderBox];
    return renderBox;
  }

  function removeSelected() {
    if (!selected.value) return false;
    const { label, id } = selected.value;
    const bucket = regions.value[label];
    if (!bucket) return false;
    const next = bucket.filter((entry) => entry.id !== id);
    if (next.length === bucket.length) return false;
    const clone: SlideRegionDict = { ...regions.value };
    if (next.length === 0) {
      delete clone[label];
    } else {
      clone[label] = next;
    }
    regions.value = clone;
    renderBoxes.value = renderBoxes.value.filter((box) => box.id !== id);
    selected.value = null;
    return true;
  }

  function hitTestAtPoint(point: SlideCanvasPoint) {
    const boxes = renderBoxes.value;
    for (let i = boxes.length - 1; i >= 0; i--) {
      if (isPointInBox(point, boxes[i])) {
        return boxes[i];
      }
    }
    return null;
  }

  function selectAtPoint(point: SlideCanvasPoint) {
    const candidate = hitTestAtPoint(point);
    if (candidate) {
      selected.value = { label: candidate.label, id: candidate.id };
      return candidate;
    }
    selected.value = null;
    return null;
  }

  function updateBoxLabel(boxId: string, nextLabel: string) {
    if (!boxId || !nextLabel) return false;
    const box = renderBoxes.value.find((candidate) => candidate.id === boxId);
    if (!box) return false;
    if (box.label === nextLabel) return true;
    const source = regions.value[box.label];
    if (!source) return false;
    const entry = source.find((candidate) => candidate.id === boxId);
    if (!entry) return false;

    const clone: SlideRegionDict = {
      ...regions.value,
      [box.label]: source.filter((candidate) => candidate.id !== boxId),
      [nextLabel]: [...(regions.value[nextLabel] ?? []), entry],
    };

    if (!clone[box.label]?.length) {
      delete clone[box.label];
    }

    regions.value = clone;
    selected.value = { id: entry.id, label: nextLabel };
    rebuildRenderBoxes();
    return true;
  }

  function updateBoxRotation(boxId: string, label: string, rotation: number) {
    if (!boxId || !label) return false;
    const sourceRegions = regions.value[label];
    if (!sourceRegions || !sourceRegions.some((entry) => entry.id === boxId)) return false;

    const nextRotation = normalizeRotation(rotation);

    regions.value = {
      ...regions.value,
      [label]: sourceRegions.map((item) =>
        item.id === boxId ? { ...item, rotation: nextRotation } : item,
      ),
    };

    renderBoxes.value = renderBoxes.value.map((item) =>
      item.id === boxId ? { ...item, rotation: nextRotation } : item,
    );

    return true;
  }

  function updateBoxGeometry(
    boxId: string,
    label: string,
    geometry: { x: number; y: number; width: number; height: number },
  ) {
    if (!boxId || !label) return false;
    const sourceRegions = regions.value[label];
    if (!sourceRegions) return false;

    const nextBounds = rectToBounds(geometry as SlideCanvasBox);

    regions.value = {
      ...regions.value,
      [label]: sourceRegions.map((item) =>
        item.id === boxId ? { ...item, bounds: nextBounds } : item,
      ),
    };

    renderBoxes.value = renderBoxes.value.map((item) =>
      item.id === boxId ? { ...item, ...geometry } : item,
    );

    return true;
  }

  function setLabelFilter(next: LabelFilterMap) {
    labelFilter = next && Object.keys(next).length > 0 ? { ...next } : null;
    rebuildRenderBoxes();
  }

  function serializeRegions() {
    return { ...regions.value };
  }

  function serializeBoxes() {
    return renderBoxes.value.map((box) => ({
      id: box.id,
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      rotation: box.rotation,
    }));
  }

  const selectedRenderBox = computed(() => {
    const s = selected.value;
    if (!s) return null;
    return renderBoxes.value.find((b) => b.id === s.id && b.label === s.label) ?? null;
  });

  return {
    regions,
    renderBoxes,
    selected,
    selectedRenderBox,
    addBox,
    removeSelected,
    loadRegionsDict,
    selectAtPoint,
    serializeRegions,
    serializeBoxes,
    setLabelFilter,
    updateBoxLabel,
    updateBoxRotation,
    updateBoxGeometry,
    hitTestAtPoint,
  };
}
